import { useRef, useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import AnimatedSection from './AnimatedSection';

interface Testimonial {
  id: string;
  name: string;
  organisation: string | null;
  quote: string;
  rating: number | null;
  featured: boolean;
}

const TestimonialCarousel = () => {
  const { data: testimonials } = useApi<Testimonial[]>('/testimonials');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPaused, setIsPaused] = useState(false);

  const items = testimonials && testimonials.length > 0
    ? testimonials
    : [
        { id: '1', name: 'Event Organiser', organisation: 'Agricultural Show', quote: 'Jurassic Jack brought the whole showgrounds to life. Families followed the Jeep, kids lined up for dinosaur encounters, and the energy carried all day.', rating: 5, featured: true },
        { id: '2', name: 'Event Coordinator', organisation: 'Community Festival', quote: 'Professional, safe, and incredibly engaging. It wasn\'t just entertainment \u2014 it was a full activation that kept people moving and smiling.', rating: 5, featured: true },
        { id: '3', name: 'School Principal', organisation: 'Primary School', quote: 'The students were absolutely captivated. It sparked curiosity and imagination in a way that lasted well beyond the visit.', rating: 5, featured: false },
      ];

  useEffect(() => {
    if (!scrollRef.current || isPaused || items.length <= 1) return;
    const interval = setInterval(() => {
      const el = scrollRef.current;
      if (!el) return;
      const cardWidth = el.firstElementChild?.clientWidth || 320;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft >= maxScroll - 10) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: cardWidth + 16, behavior: 'smooth' });
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, items.length]);

  return (
    <section className="py-20 bg-jungle-900 relative overflow-hidden">
      {/* Subtle electric fence pattern */}
      <div className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 60px, rgba(255,191,0,0.3) 60px, rgba(255,191,0,0.3) 61px)',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <AnimatedSection className="text-center mb-14">
          <p className="font-mono text-sm uppercase tracking-[0.2em] text-amber mb-2">
            Testimonials
          </p>
          <h2 className="text-4xl md:text-5xl font-display text-cream">
            What Organisers And Families <span className="text-amber">Say</span>
          </h2>
        </AnimatedSection>

        <div
          ref={scrollRef}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4
                     -mx-4 px-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {items.map((t) => (
            <div
              key={t.id}
              className="snap-center shrink-0 w-[320px] md:w-[380px]
                         bg-jungle-800/50 backdrop-blur-sm rounded-xl p-6
                         border border-jungle-700 hover:border-amber/30
                         transition-all duration-300"
            >
              {/* Quote icon */}
              <svg className="w-8 h-8 text-jp-red/60 mb-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151C7.546 6.068 5.983 8.789 5.983 11H10v10H0z" />
              </svg>

              <p className="text-cream/80 text-sm leading-relaxed mb-4 italic">
                "{t.quote}"
              </p>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-display text-sm text-cream font-bold">{t.name}</p>
                  {t.organisation && (
                    <p className="text-sm text-amber/60">{t.organisation}</p>
                  )}
                </div>
                {t.rating && (
                  <div className="flex gap-0.5">
                    {[...Array(t.rating)].map((_, i) => (
                      <span key={i} className="text-amber text-sm">★</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
